/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, type PropsWithChildren, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import WishlistIcon from './wishlist-icon';
import HeaderUtilityBar from './utility-bar';
import { useTranslation } from 'react-i18next';
import logo from '/images/logo.svg';
import { Button } from '@/components/ui/button';
import { SparklesIcon } from '@/components/icons';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import { Component } from '@/lib/decorators/component';
import { RegionDefinition } from '@/lib/decorators';

@Component('header', {
    name: 'Header',
    group: 'Layout',
    description: 'Global site header with navigation, search, and cart',
    embedded: true,
    component_id: 'header',
})
@RegionDefinition([{ id: 'announcement', name: 'Announcement' }])
export class HeaderMetadata {}

/**
 * SRAM logo lockup — the white `logo.svg` wordmark inset in a red chip.
 *
 * `logo.svg` is a WHITE wordmark shared with the (dark) footer, so on this light
 * header it is legible only inside the red chip. This is also what `brand-assets`
 * already requires: the red treatment comes from the container's background, not
 * from a second baked-in asset.
 *
 * Figma's logo node (Top Navigation `0:35`) is a 130.83x40 box whose wordmark is
 * inset 13.33px on every side — exactly one third of the lockup height. Driving
 * both the chip height and that inset off a single `--header-logo-height` keeps
 * everything to one value instead of four magic numbers. Fixed at 40px (2.5rem)
 * — Figma's own value — at every breakpoint; it does not shrink on smaller
 * screens, and matches the search field's height (see `Search`, `size="md"`).
 *
 * The chip colour is `--header-logo-background`, a HEADER token rather than
 * `--brand`, so the masthead stays decoupled from the core palette (see
 * theme/tokens/header.css).
 */
const LOGO_CHIP_CLASS =
    'flex-shrink-0 flex items-center justify-center bg-header-logo-background ' +
    '[--header-logo-height:2.5rem] h-[var(--header-logo-height)] p-[calc(var(--header-logo-height)/3)]';

const LOGO_WORDMARK_CLASS = 'h-[calc(var(--header-logo-height)/3)] w-auto [filter:var(--header-logo-filter)]';

interface HeaderProps extends PropsWithChildren {
    /** Leading promo/announcement copy, rendered in the utility bar's left column. */
    beforeHeader?: ReactNode;
    /**
     * Slot rendered above the header's main row. Used for announcement banners
     * or other above-the-fold content that should sit at the top of the page.
     */
    announcementSlot?: ReactNode;
    variant?: 'full' | 'checkout';
}

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch() {
    const location = useLocation();
    return <Search key={`${location.pathname}${location.search}`} />;
}

export default function Header({
    children,
    beforeHeader,
    announcementSlot,
    variant = 'full',
}: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const config = useConfig();
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'checkout') {
        return (
            <header
                ref={headerRef}
                className="bg-header-background text-header-foreground border-b border-header-border sticky top-0 z-50">
                <div className="section-container">
                    <div className="flex items-center h-16">
                        {/* The chip applies here too — the wordmark is white, so the
                            checkout logo would otherwise render white-on-white. */}
                        <Link to="/" className={LOGO_CHIP_CLASS} data-testid="header-logo">
                            <img src={logo} alt={t('logoAlt')} className={LOGO_WORDMARK_CLASS} />
                        </Link>
                        <div className="flex-1" />
                        <CartBadge />
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header
            ref={headerRef}
            className="bg-header-background text-header-foreground border-b border-header-border sticky top-0 z-50">
            {/* Utility bar is header chrome — always present in the full variant, never
                in checkout. `beforeHeader` supplies its leading promo column. */}
            <HeaderUtilityBar promo={beforeHeader} />
            {announcementSlot}
            {/* py-5 around the 2.5rem lockup reproduces Figma's 80px main row.
             *
             * Container geometry deliberately does NOT match Figma. The masthead defines
             * `layout/max-width: 1440` and `layout/padding-x: 32`; `section-container`
             * resolves to 1536 / 64. The header keeps `section-container` so it stays
             * aligned with the page content beneath it — adopting Figma's values here
             * alone would misalign the masthead against every other section, which is
             * worse than a uniform delta from the design file. Whether 1440/32 should
             * govern the storefront as a whole is a site-wide question, not a header one. */}
            <div className="section-container py-5">
                {/* Top row: Logo left, Icons right */}
                {/* Column gap steps down through the md tier (tightest fit: mega menu + inline
                    search + icons all compete for room) and reopens at lg once there's more
                    width to spare — see the `site-header` spec's responsive tier requirement. */}
                <div className="flex items-center gap-x-1 md:gap-x-3 lg:gap-x-6">
                    {/* Logo — white wordmark inside the red chip (see LOGO_CHIP_CLASS) */}
                    <Link to="/" className={LOGO_CHIP_CLASS} data-testid="header-logo">
                        <img src={logo} alt={t('logoAlt')} className={LOGO_WORDMARK_CLASS} />
                    </Link>

                    {/* Mega menu — desktop tier only (>= md, 768px); tablet and mobile share the
                        hamburger/mobile menu instead (see the `site-header` spec's three-tier
                        responsive navigation requirement). */}
                    <div className="hidden md:flex items-center">{children}</div>

                    {/* Spacer - takes remaining space */}
                    <div className="flex-1" />

                    {/* Search — inline from the tablet tier up (>= sm, 640px). Below that,
                        search wraps to its own row (see the mobile search block below). */}
                    <div className="hidden sm:block" data-testid="header-search-desktop">
                        <LocationKeyedSearch />
                    </div>

                    {/* Icons group - includes mobile hamburger.
                        Figma masthead order (nodes 0:71 / 0:74 / 0:77):
                        list_alt -> shopping_cart -> account_circle.
                        Each icon button is now a fixed 40x40 box (`size="icon-md"`), so spacing
                        between them comes from this container's gap, not button padding: 8px
                        through the md tier, 16px from lg up. */}
                    <div className="flex items-center gap-2 lg:gap-4">
                        {showChat && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="cursor-pointer lg:px-4 px-1 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                                onClick={() => launchChat()}
                                aria-label={t('openChat')}>
                                <SparklesIcon />
                            </Button>
                        )}
                        <WishlistIcon />
                        {/* Generic extension slot, kept for extensibility. The SRAM
                            masthead shows no extension icons here. */}
                        <UITarget targetId="sfcc.header.before.cart" />
                        <CartBadge />
                        <UserActions />
                        {/* Hamburger trigger — mobile and tablet tiers (< md, below 768px). */}
                        <div className="md:hidden">{children}</div>
                    </div>
                </div>

                {/* Mobile search — own row below the primary row, mobile tier only (< sm, below
                    640px). Tablet and desktop render search inline above instead. */}
                <div className="pt-4 sm:hidden" data-testid="header-search-mobile">
                    <LocationKeyedSearch />
                </div>
                <UITarget targetId="sfcc.header.bnpl.banner" />
            </div>
        </header>
    );
}
